"use client"

import styles from "./styles.module.css"
import { useEffect, useState, forwardRef, useImperativeHandle } from "react"
import Input from "../../modules/Input/Input"
import Select from "../../modules/Select/Select"
import DatePicker from "../../../shared-modules/DatePicker/DatePicker"
import { parseDateSafe, formatDateOnly } from "../../../../utils/dateFormatters"
import {
  getDeviceTypes,
  getWarehouseStatuses,
  createWarehouseDevice,
} from "../../../../services/inventory/detail"
import { useForm, Controller } from "react-hook-form"

const AddIndividualDevice = forwardRef(({ onSuccess }, ref) => {
  const [deviceTypeOptions, setDeviceTypeOptions] = useState([])
  const [deviceStatusOptions, setDeviceStatusOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")
  const [selectedDeviceType, setSelectedDeviceType] = useState(null)

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm()

  // Fetch device types + statuses
  useEffect(() => {
    const fetchData = async () => {
      const types = await getDeviceTypes()
      const statuses = await getWarehouseStatuses()

      if (types) {
        setDeviceTypeOptions(types.map((t) => ({ label: t.name, value: t.id })))
      }
      if (statuses) {
        const filteredStatuses = statuses.inventory_statuses.filter(
          (s) => s !== "Distributed"
        )
        setDeviceStatusOptions(
          filteredStatuses.map((s) => ({ label: s, value: s }))
        )
      }
    }
    fetchData()
  }, [])

  // Validation patterns
  const imeiRegex = /^\d{15}$/ // plain 15 digits only
  const gatewayRegex = /^[0-9A-F]{4}\s[0-9A-F]{4}\s[0-9A-F]{4}$/i

  // Format input as user types
  const formatSerial = (val) => {
    if (selectedDeviceType?.label === "Tenovi Hub") {
      // Gateway ID formatting
      const clean = val.replace(/[^0-9a-fA-F]/g, "").toUpperCase().slice(0, 12)
      return clean.match(/.{1,4}/g)?.join(" ") || ""
    } else {
      // IMEI formatting: just digits, max 15
      return val.replace(/\D/g, "").slice(0, 15)
    }
  }

  const onSubmit = async (data) => {
    const payload = {
      serial: data.serial_no,
      device_type: data.device_type,
      inventory_status: data.inventory_status,
      ...(data.added_at && { added_at: parseDateSafe(data.added_at)?.toISOString() || new Date().toISOString() }),
      // purchase_date: send DATE ONLY (no time component)
      ...(data.purchase_date && { purchase_date: formatDateOnly(data.purchase_date) }),
    }

    setLoading(true)
    setMessage("")

    const result = await createWarehouseDevice(payload)

    if (result && result.success) {
      setMessage("")
      if (onSuccess) onSuccess(result.data)
    } else {
      setMessage(" Failed to add device")
    }

    setLoading(false)
  }

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)()
    },
    resetForm: () => {
      reset()
      setMessage("")
    },
    isLoading: loading,
  }))

  return (
    <section className={styles.individualContainer}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Device Type */}
        <Controller
          name="device_type"
          control={control}
          rules={{ required: "Device Type is required" }}
          render={({ field }) => (
            <div className={styles.input_field}>
              <label>
                Device Type <span className={styles.required}>*</span>
              </label>
              <Select
                options={deviceTypeOptions}
                value={field.value}
                onChange={(val) => {
                  field.onChange(val)
                  setSelectedDeviceType(
                    deviceTypeOptions.find((opt) => opt.value === val)
                  )
                  setValue("serial_no", "") // reset serial on change
                }}
                placeholder="Select type"
                width="100%"
              />
              {errors.device_type && (
                <p className={styles.error}>{errors.device_type.message}</p>
              )}
            </div>
          )}
        />

        {/* Serial Number */}
        <Controller
          name="serial_no"
          control={control}
          rules={{
            required: "Serial No is required",
            validate: (val) => {
              if (!selectedDeviceType) return "Select device type first"
              if (selectedDeviceType.label === "Tenovi Hub") {
                return (
                  gatewayRegex.test(val) || "Format: XXXX XXXX XXXX (hex)"
                )
              } else {
                return imeiRegex.test(val) || "IMEI must be 15 digits"
              }
            },
          }}
          render={({ field }) => (
            <div className={styles.input_field}>
              <label>
                Serial No <span className={styles.required}>*</span>
              </label>
              <Input
                type="text"
                {...field}
                disabled={!selectedDeviceType}
                placeholder={
                  !selectedDeviceType
                    ? "Select device type first"
                    : selectedDeviceType.label === "Tenovi Hub"
                    ? "XXXX XXXX XXXX"
                    : "IMEI: 15 digits"
                }
                value={field.value || ""}
                onChange={(e) => field.onChange(formatSerial(e.target.value))}
              />
              {errors.serial_no && (
                <p className={styles.error}>{errors.serial_no.message}</p>
              )}
            </div>
          )}
        />

        {/* Purchase Date (required) */}
        <Controller
          name="purchase_date"
          control={control}
          rules={{ required: "Purchase date is required" }}
          render={({ field }) => (
            <div className={styles.input_field}>
              <label>
                Purchase Date <span className={styles.required}>*</span>
              </label>
              <DatePicker
                selectedDate={field.value}
                onDateSelect={field.onChange}
                placeholder="Select purchase date"
              />
              {errors.purchase_date && (
                <p className={styles.error}>{errors.purchase_date.message}</p>
              )}
            </div>
          )}
        />

        {/* Inventory Status */}
        <Controller
          name="inventory_status"
          control={control}
          rules={{ required: "Inventory Status is required" }}
          render={({ field }) => (
            <div className={styles.input_field}>
              <label>Inventory Status <span className={styles.required}>*</span></label>
              <Select
                options={deviceStatusOptions}
                value={field.value}
                onChange={field.onChange}
                placeholder="Select status"
                width="100%"
              />
              {errors.inventory_status && (
                <p className={styles.error}>
                  {errors.inventory_status.message}
                </p>
              )}
            </div>
          )}
        />

        {/* Added At (optional, remains separate) */}
        <Controller
          name="added_at"
          control={control}
          render={({ field }) => (
            <div className={styles.input_field}>
              <label>Added At (Optional)</label>
              <DatePicker
                selectedDate={field.value}
                onDateSelect={field.onChange}
                placeholder="Select date"
              />
            </div>
          )}
        />

        {message && <p className={styles.message}>{message}</p>}
      </form>
    </section>
  )
})

AddIndividualDevice.displayName = "AddIndividualDevice"

export default AddIndividualDevice
